import { useCallback } from 'react';

import { useNotifications } from '@gasbi/shared/common/providers/NotificationProvider/hooks/useNotifications';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { commercialMeteringPointsApi } from '@/features/commercialMeteringPoint/commercialMeteringPointsApi';
import {
  useCreateViolationActsMutation,
  useUpdateViolationActsMutation,
} from '@/features/violationActs/violationActsApi';
import { getErrorMessages } from '@/pages/ViolationActForm/hooks/submitFormUtils/getErrorMessages';
import { prepareRequestBody } from '@/pages/ViolationActForm/hooks/submitFormUtils/prepareRequestBody';

import { ActFormContextInitialDataSubmitMethod } from '@components/ActsAndDocumentsForm';
import { isFileLikeObject } from '@components/ActsAndDocumentsForm/utils/isFileLikeObject';

import { ViolationActFormStep } from '../';

type SubmitFormProps = {
  isEditMode?: boolean;
  currentSteps: typeof ViolationActFormStep;
};

export const useSubmitForm = ({
  isEditMode,
  currentSteps,
}: SubmitFormProps) => {
  const { push } = useNotifications();
  const dispatch = useDispatch();
  const { meteringPointId, commercialMeteringPointId } = useParams<{
    meteringPointId: string;
    commercialMeteringPointId: string;
  }>();

  const mutationsQuery = isEditMode
    ? useUpdateViolationActsMutation
    : useCreateViolationActsMutation;

  const [mutation] = mutationsQuery();

  const submit: ActFormContextInitialDataSubmitMethod = useCallback(
    async ({ stepsState, selectedFiles, documentsToDelete }) => {
      const requestBody = prepareRequestBody({
        stepsState,
        meteringPointId,
        commercialMeteringPointId,
        steps: currentSteps,
        isEditMode,
        documentsToDelete,
      });

      const formData = new FormData();

      formData.append('id', requestBody.id);
      formData.append('json', JSON.stringify(requestBody));

      if (selectedFiles.length > 0) {
        const files = selectedFiles.filter(
          (file): file is File => !isFileLikeObject(file),
        );
        files.forEach(file => formData.append('documents', file));
      }

      try {
        await mutation(formData).unwrap();
        dispatch(
          commercialMeteringPointsApi.util.invalidateTags([
            'CommercialMeteringPointDocuments',
          ]),
        );
        const message = isEditMode
          ? 'Акт про порушення успішно відредаговано'
          : 'Акт про порушення успішно створено';

        push({
          message,
          type: 'success',
          options: {
            duration: 3000,
          },
        });

        return true;
      } catch (e) {
        const pushMessageFail = `Помилка${isEditMode ? ' редагування' : ' створення'}`;
        const errorMessages = getErrorMessages(e, pushMessageFail);
        push({
          message: Array.isArray(errorMessages)
            ? errorMessages[0]
            : errorMessages,
          type: 'danger',
        });
        return false;
      }
    },
    [
      dispatch,
      currentSteps,
      meteringPointId,
      commercialMeteringPointId,
      isEditMode,
      mutation,
      push,
    ],
  );

  return { submit };
};
